import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ApiError, type CallbackSlotView, type PublicEnquirySourceView } from "@keydials/api-client";
import { readableTextColour } from "@keydials/dial-renderer";
import { isPlausibleEmail } from "@keydials/ui";
import { api } from "../auth/AuthContext";

/**
 * The public site's own tokens, measured from www.keydials.com rather than guessed, because this page is
 * the only one a non-customer ever sees and it should read as the same company.
 *
 * Deliberately hard-coded rather than pulled from the app theme: the theme follows the reader's dark-mode
 * preference, and a partner picks their accent against a LIGHT ground. Pinning the page light keeps their
 * choice predictable — and it is what removed the reported bug's root cause, since the dark theme's
 * --on-accent is dark ink.
 */
const SITE = {
  ground: "#F8FAFC",
  card: "#FFFFFF",
  border: "#E4E9F0",
  ink: "#1E1D51",
  body: "#4B494B",
  muted: "#6B6A72",
  accent: "#22C55E",
  font: "Poppins, 'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
} as const;

/**
 * Where "back" goes.
 *
 * This page is a front door for people with no account, and it was a cul-de-sac: no header, no
 * navigation, nothing but the form — somebody who clicked "Book a walkthrough" and then thought
 * better of it had no way back to the site they came from except the browser's own button.
 *
 * The `from` token the site already sends for attribution is enough to name the page they left, so it
 * is reused rather than a second parameter being invented alongside it. Unrecognised tokens fall back
 * to the site root: the token arrives in a URL anybody can edit, and it must never be able to compose
 * a path of its own.
 */
const SITE_URL =
  (((import.meta.env as Record<string, string | undefined>).VITE_SITE_URL ?? "").trim().replace(/\/+$/, "") ||
    "https://www.keydials.com");

/** Bare `from` tokens that name a page of their own. Anything else lands on the homepage. */
const SITE_PAGES = new Set([
  "about",
  "contact",
  "dials",
  "how-it-works",
  "mobile",
  "partners",
  "pricing",
  "sectors",
  "what-it-is",
]);

function backToSitePath(sourcePage: string | null): string {
  if (!sourcePage) return "/";
  const separator = sourcePage.indexOf(":");
  const head = separator === -1 ? sourcePage : sourcePage.slice(0, separator);
  const tail = separator === -1 ? "" : sourcePage.slice(separator + 1);
  // A slug is the only thing allowed through into a path — never the raw tail.
  const slug = /^[a-z0-9][a-z0-9-]*$/.test(tail) ? tail : "";
  switch (head) {
    case "sector":
      return slug ? `/sectors/${slug}` : "/sectors";
    case "dial":
      return slug ? `/dials/${slug}` : "/dials";
    case "partner":
    case "partners":
      return slug ? `/partners/${slug}` : "/partners";
    case "header":
      return slug === "sectors" || slug === "partners" ? `/${slug}` : "/";
    case "home":
      return "/";
    default:
      return SITE_PAGES.has(head) ? `/${head}` : "/";
  }
}

/** "Tuesday 25 August" — the visitor's own zone, which is where they'll take the call. */
function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function formatSlot(iso: string): string {
  return `${formatDay(iso)} at ${formatTime(iso)}`;
}

/**
 * The visitor's own IANA zone, sent with the booking so the confirmation email is written in the same
 * zone the times above were offered in. Undefined where the browser can't name one — the server then
 * falls back to the organisation's zone rather than guessing.
 */
function visitorTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/** Slots grouped under their day heading, in offer order. */
function groupByDay(slots: CallbackSlotView[]): [string, CallbackSlotView[]][] {
  const groups = new Map<string, CallbackSlotView[]>();
  for (const slot of slots) {
    const day = formatDay(slot.startUtc);
    const bucket = groups.get(day);
    if (bucket) bucket.push(slot);
    else groups.set(day, [slot]);
  }
  return [...groups.entries()];
}

/**
 * The public enquiry page (KEY-313/KEY-314, spec § 05–06): /enquire/<sourceKey>, linked from a business
 * website's Contact Us button. Renders before any auth gating — the visitor has no session and never
 * gets one. The page wears the source's branding, and the flow is: details → emailed six-digit code →
 * pick a call-back time, where the entry point offers one → confirmation. A dead or deactivated key
 * shows a dead-end rather than guessing at a destination (spec D5).
 */
/**
 * Poppins is the marketing site's face, and this page is the only place in the app that wants it — so it
 * is fetched here instead of in index.html, where it would cost every authenticated page a font it never
 * uses. Idempotent: React strict mode mounts twice in development.
 */
function useSiteFont() {
  useEffect(() => {
    const id = "kd-poppins";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);
}

export function PublicEnquiry() {
  useSiteFont();
  const location = useLocation();
  const sourceKey = location.pathname.split("/")[2] ?? "";
  // Where on the marketing site they clicked from (KEY-390). Read once, and only kept if it looks
  // like the short tokens the site sends — this arrives in a URL anybody can edit, and it ends up in
  // front of CRM staff, so it is not a free-text channel into the pipeline.
  const sourcePage = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("from");
    return raw && /^[a-z0-9][a-z0-9:/-]{0,79}$/.test(raw) ? raw : null;
  }, []);

  const [source, setSource] = useState<PublicEnquirySourceView | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [step, setStep] = useState<"form" | "verify" | "slots" | "done">("form");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const honeypot = useRef<HTMLInputElement>(null);

  const [enquiryToken, setEnquiryToken] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Call-back offer (KEY-314). An empty list is a legitimate answer — nobody free, or this entry point
  // doesn't offer calls — and the page simply confirms instead of offering times.
  const [slots, setSlots] = useState<CallbackSlotView[] | null>(null);
  const [bookedFor, setBookedFor] = useState<string | null>(null);
  const [agentName, setAgentName] = useState<string | null>(null);

  useEffect(() => {
    if (!sourceKey) {
      setInvalid(true);
      return;
    }
    let cancelled = false;
    api.getPublicEnquirySource(sourceKey)
      .then((s) => !cancelled && setSource(s))
      .catch(() => !cancelled && setInvalid(true));
    return () => {
      cancelled = true;
    };
  }, [sourceKey]);

  // A partner's own colour when they set one, otherwise the Key Dials green from the site — the old
  // default was an indigo belonging to neither.
  const accent = source?.accentColour?.trim() || SITE.accent;
  // THE BUG: .kd-btn-primary takes its text from --on-accent, and the page only ever overrode the
  // background — so a dark accent landed dark-on-dark. Computed from the accent actually in use, so it
  // stays right whatever a partner picks.
  const onAccent = readableTextColour(accent, "#ffffff", SITE.ink);
  // The server's plausibility rule, applied before the round-trip (KEY-423).
  const canSubmit = firstName.trim() !== "" && isPlausibleEmail(email) && message.trim() !== "";

  async function submit() {
    if (busy || !canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.submitPublicEnquiry(sourceKey, {
        firstName: firstName.trim(),
        lastName: lastName.trim() || null,
        email: email.trim(),
        phone: phone.trim() || null,
        message: message.trim(),
        marketingOptIn,
        website: honeypot.current?.value || null,
        sourcePage,
      });
      setEnquiryToken(res.enquiryToken);
      setStep("verify");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sending your enquiry failed — please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (busy || code.trim().length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.verifyPublicEnquiry(enquiryToken, code.trim());
      if (res.verified) {
        // Verified: see whether there are times to offer before settling for "we'll be in touch".
        try {
          const offer = await api.getPublicCallbackSlots(enquiryToken);
          setSlots(offer.slots);
          setStep(offer.slots.length > 0 ? "slots" : "done");
        } catch {
          setStep("done");
        }
      } else {
        setError("That code didn't match. Check the most recent email — codes expire after 10 minutes.");
        setCode("");
      }
    } catch {
      setError("Checking the code failed — please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function book(startUtc: string) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.bookPublicCallback(enquiryToken, startUtc, visitorTimeZone());
      if (res.booked) {
        setBookedFor(res.startUtc);
        setAgentName(res.agentName);
        setStep("done");
      } else {
        // Someone took it between the offer and the click — re-offer rather than showing an error.
        setError("Sorry, that time has just gone. Here are the times still free.");
        const offer = await api.getPublicCallbackSlots(enquiryToken);
        setSlots(offer.slots);
        if (offer.slots.length === 0) setStep("done");
      }
    } catch {
      setError("Booking that time didn't work — please try another.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      className="kd-public"
      // The accent is published as a custom property so the scoped CSS above can tint focus rings and
      // hovers with it, rather than every control needing an inline style.
      style={{
        ["--kd-accent" as string]: accent,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: SITE.ground,
        color: SITE.body,
        fontFamily: SITE.font,
        // colorScheme pins the LIGHT form controls too: without it a visitor in dark mode gets a dark
        // native input on a white card, which looks broken rather than themed.
        colorScheme: "light",
      }}
    >
      {/* The way out. Quiet and left-aligned, because on a partner's enquiry the partner is the host
          here and we are a mark in the corner — but present on every step, including the dead end an
          invalid link lands on. */}
      <header style={{ width: "100%", maxWidth: 1120, margin: "0 auto", padding: "20px 20px 0" }}>
        <a
          href={`${SITE_URL}${backToSitePath(sourcePage)}`}
          aria-label="Back to the Key Dials website"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: "-0.2px",
            color: SITE.ink,
            textDecoration: "none",
          }}
        >
          <span aria-hidden style={{ fontSize: 17, lineHeight: 1 }}>&#8592;</span>
          Key Dials
        </a>
      </header>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "28px 20px 36px",
        }}
      >
      <div
        style={{
          width: "min(520px, 100%)",
          background: SITE.card,
          border: `1px solid ${SITE.border}`,
          borderRadius: 14,
          // The site leans on soft elevation over heavy borders; this is the same idea at card scale.
          boxShadow: "0 1px 2px rgba(30,29,81,.04), 0 12px 32px rgba(30,29,81,.06)",
          padding: "36px 32px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {invalid ? (
          <>
            <h1 style={{ margin: 0, fontSize: 29, lineHeight: 1.18, letterSpacing: "-0.7px", fontWeight: 700, color: SITE.ink }}>This enquiry link isn&rsquo;t valid</h1>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: SITE.body }}>
              The link may have been replaced. Head back to the website you came from and use its contact page.
            </p>
          </>
        ) : source === null ? (
          <p style={{ margin: 0, fontSize: 15, color: SITE.muted }}>Loading…</p>
        ) : step === "done" ? (
          <>
            <h1 style={{ margin: 0, fontSize: 29, lineHeight: 1.18, letterSpacing: "-0.7px", fontWeight: 700, color: SITE.ink }}>Thanks, {firstName.trim()} — you&rsquo;re all set</h1>
            {bookedFor ? (
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: SITE.body }}>
                {agentName ?? "Someone"} from {source.displayName} will call you on{" "}
                <strong>{formatSlot(bookedFor)}</strong>. We&rsquo;ve emailed you a confirmation.
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: SITE.body }}>
                Your enquiry is with {source.displayName} and they&rsquo;ll be in touch shortly.
              </p>
            )}
          </>
        ) : step === "slots" ? (
          <>
            <h1 style={{ margin: 0, fontSize: 29, lineHeight: 1.18, letterSpacing: "-0.7px", fontWeight: 700, color: SITE.ink }}>When suits you for a call?</h1>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: SITE.body }}>
              Pick a time and {source.displayName} will call you then. Times are shown in your own time
              zone. Prefer not to book? Just close this page — your enquiry is already with them.
            </p>
            {error && <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: "#B42318" }}>{error}</p>}
            <div style={{ display: "flex", flexDirection: "column", gap: 14, maxHeight: 340, overflowY: "auto" }}>
              {groupByDay(slots ?? []).map(([day, daySlots]) => (
                <div key={day}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 6 }}>
                    {day}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {daySlots.map((s) => (
                      <button
                        key={s.startUtc}
                        type="button"
                        disabled={busy}
                        onClick={() => void book(s.startUtc)}
                        className="kd-btn-secondary"
                        style={{ padding: "9px 14px", fontSize: 14, fontWeight: 500, borderRadius: 8, border: `1px solid ${SITE.border}`, background: SITE.card, color: SITE.ink, cursor: busy ? "default" : "pointer" }}
                      >
                        {formatTime(s.startUtc)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setStep("done")} style={{ background: "none", border: "none", padding: 0, fontSize: 13.5, color: SITE.muted, textDecoration: "underline", cursor: "pointer", alignSelf: "flex-start", fontFamily: "inherit" }}>
              No thanks — just get in touch when you can.
            </button>
          </>
        ) : step === "verify" ? (
          <>
            <h1 style={{ margin: 0, fontSize: 29, lineHeight: 1.18, letterSpacing: "-0.7px", fontWeight: 700, color: SITE.ink }}>Check your email</h1>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: SITE.body }}>
              We&rsquo;ve sent a six-digit code to <strong>{email.trim()}</strong>. Enter it below to confirm your enquiry.
            </p>
            <input
              className="field"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && void verify()}
              placeholder="123456"
              style={{ fontSize: 22, letterSpacing: 6, textAlign: "center" }}
              autoFocus
            />
            {error && <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: "#B42318" }}>{error}</p>}
            <button className="kd-btn-primary" type="button" disabled={busy || code.length !== 6} onClick={() => void verify()} style={{ background: accent, borderColor: accent, color: onAccent }}>
              {busy ? "Checking…" : "Confirm"}
            </button>
            <button type="button" onClick={() => { setStep("form"); setError(null); }} style={{ background: "none", border: "none", padding: 0, fontSize: 13, color: "var(--text-muted)", textDecoration: "underline", cursor: "pointer" }}>
              Didn&rsquo;t get it? Check spam, or go back and send again.
            </button>
          </>
        ) : (
          <>
            {source.logoUrl && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 2 }}>
                <img src={source.logoUrl} alt={source.displayName} style={{ maxHeight: 44, alignSelf: "flex-start", maxWidth: "70%", objectFit: "contain" }} />
                {/* A short accent rule rather than a full divider — the site uses colour to mark a
                    section start, and a full-width line would cut the card in half. */}
                <span aria-hidden style={{ width: 44, height: 3, borderRadius: 2, background: accent }} />
              </div>
            )}
            <h1 style={{ margin: 0, fontSize: 29, lineHeight: 1.18, letterSpacing: "-0.7px", fontWeight: 700, color: SITE.ink }}>
              Get in touch with {source.displayName}
            </h1>
            {source.introText && <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: SITE.body }}>{source.introText}</p>}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <input className="field" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" autoComplete="given-name" />
              <input className="field" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" autoComplete="family-name" />
            </div>
            <input className="field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" autoComplete="email" />
            <input className="field" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" autoComplete="tel" />
            <textarea className="field" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What would you like to talk about?" />

            {/* Honeypot: invisible to people, tempting to bots. Never referenced by label or tab order. */}
            <input ref={honeypot} type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: -9999, width: 1, height: 1, opacity: 0 }} />

            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "var(--text-muted)" }}>
              <input type="checkbox" checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)} style={{ marginTop: 2 }} />
              <span>Also send me occasional updates. Optional — unsubscribe any time.</span>
            </label>

            {error && <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: "#B42318" }}>{error}</p>}

            <button className="kd-btn-primary" type="button" disabled={busy || !canSubmit} onClick={() => void submit()} style={{ background: accent, borderColor: accent, color: onAccent }}>
              {busy ? "Sending…" : "Send enquiry"}
            </button>

            <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, color: "var(--text-faint, var(--text-muted))" }}>
              We&rsquo;ll use these details to respond to your enquiry and to arrange any call you book. Your
              enquiry is held by Key Dials and, where it&rsquo;s directed to one of our partners, shared with
              that partner so they can respond. We keep enquiry records for {source.retentionMonths} months
              from our last contact, or for as long as we&rsquo;re working together, and you can ask for your
              details to be removed at any time.
            </p>
          </>
        )}
      </div>
      </div>

      <footer
        style={{
          padding: "0 20px 30px",
          textAlign: "center",
          fontSize: 12.5,
          lineHeight: 1.6,
          color: SITE.muted,
        }}
      >
        Key Dials Limited ·{" "}
        <a href={SITE_URL} style={{ color: SITE.muted, textDecoration: "underline" }}>
          keydials.com
        </a>
      </footer>
    </main>
  );
}
